import { useState, useEffect, useRef } from 'react';
import {
  X,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Loader2,
  AlertCircle,
  User,
  Tag,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  RefreshCw,
} from 'lucide-react';
import { usePermissions } from '@utils/permissions';
import { usePersonAutomations, useAutomationPolling } from '@hooks/useAutomations';
import TriggerAutomationModal from '@components/automations/TriggerAutomationModal';

// Common titles to strip when generating initials
const TITLES = ['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'rev', 'sir', 'dame', 'lord', 'lady'];

function getInitials(name: string): string {
  const parts = name.split(' ').filter(p => !TITLES.includes(p.toLowerCase()));
  if (parts.length === 0) return name.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
  return (parts[0]!.charAt(0) + parts[parts.length - 1]!.charAt(0)).toUpperCase();
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export interface PersonDetail {
  id: number;
  name: string;
  avatarUrl: string | null;
  emails: Array<{ email: string; is_primary: boolean }>;
  phoneNumbers: Array<{ number: string; is_primary: boolean; label?: string }>;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    county: string | null;
    postcode: string | null;
    country: string | null;
  } | null;
  type: string | null;
  tags: string[];
  createdAt: string | null;
  updatedAt: string | null;
  memberships: Array<{
    id: number;
    planName: string;
    status: string | null;
    startDate: string | null;
    endDate: string | null;
    autoRenew: boolean | null;
  }>;
  beaconAccountId: string | null;
}

interface PersonDetailPanelProps {
  personId: number | null;
  personName?: string; // For showing name while loading
  detail: PersonDetail | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
}

function MembershipStatusBadge({ status }: { status: string | null }) {
  const s = status?.toLowerCase();
  if (s === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 className="h-3 w-3" />
        Active
      </span>
    );
  }
  if (s === 'expired' || s === 'cancelled' || s === 'canceled') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="h-3 w-3" />
        {status}
      </span>
    );
  }
  if (s === 'pending' || s === 'trial') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <Clock className="h-3 w-3" />
        {status}
      </span>
    );
  }
  if (!status) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      {status}
    </span>
  );
}

export default function PersonDetailPanel({
  personId,
  personName,
  detail,
  isLoading,
  error,
  onClose,
}: PersonDetailPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Automation state
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [pollingRecordId, setPollingRecordId] = useState<string | null>(null);
  const { records, isLoading: loadingRecords, refresh: refreshRecords } = usePersonAutomations(personId);
  const { record: pollingRecord, isPolling } = useAutomationPolling(pollingRecordId);
  const { canEdit } = usePermissions();

  // When polling record reaches a terminal state, refresh the list and stop polling
  useEffect(() => {
    if (pollingRecord && (pollingRecord.status === 'completed' || pollingRecord.status === 'failed')) {
      setPollingRecordId(null);
      refreshRecords();
    }
  }, [pollingRecord, refreshRecords]);

  const handleTriggered = (recordId: string) => {
    setPollingRecordId(recordId);
    refreshRecords();
  };

  // Close on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Trap focus inside panel and prevent body scroll
  useEffect(() => {
    if (personId !== null) {
      document.body.style.overflow = 'hidden';
      panelRef.current?.focus();
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [personId]);

  if (personId === null) return null;

  const addressLines = detail?.address
    ? [
        detail.address.line1,
        detail.address.line2,
        detail.address.city,
        detail.address.county,
        detail.address.postcode,
        detail.address.country,
      ].filter(Boolean)
    : [];

  const beaconUrl = detail?.beaconAccountId
    ? `https://app.beaconcrm.org/account/${detail.beaconAccountId}/record/person/${personId}`
    : null;

  const displayName = detail?.name || personName || 'Loading…';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col outline-none"
        role="dialog"
        aria-modal="true"
        aria-label={`Person detail: ${displayName}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            {/* Avatar */}
            <div className="h-14 w-14 flex-shrink-0 rounded-full bg-sca-purple-100 flex items-center justify-center overflow-hidden">
              {detail?.avatarUrl ? (
                <img
                  src={detail.avatarUrl}
                  alt={displayName}
                  className="h-full w-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <span className="text-lg font-semibold text-sca-purple-600">
                  {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-sca-purple-400" /> : getInitials(displayName)}
                </span>
              )}
            </div>

            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{displayName}</h2>
              {detail?.type && (
                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {detail.type}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            {beaconUrl && (
              <a
                href={beaconUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-sca-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                title="Open in Beacon CRM"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Loading state */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-sca-purple-400" />
              <p className="text-sm text-gray-500">Loading details…</p>
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Failed to load details</p>
                <p className="text-sm text-red-600 mt-0.5">{error}</p>
              </div>
            </div>
          )}

          {detail && !isLoading && (
            <>
              {/* Contact section */}
              <section>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                  Contact
                </h3>
                <div className="space-y-2">
                  {detail.emails.length > 0 ? (
                    detail.emails.map((e, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <a
                          href={`mailto:${e.email}`}
                          className="text-sm text-sca-purple-600 hover:text-sca-purple-700 hover:underline truncate"
                        >
                          {e.email}
                        </a>
                        {e.is_primary && (
                          <span className="text-xs text-gray-400 flex-shrink-0">Primary</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-3 text-sm text-gray-400">
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      No email address
                    </div>
                  )}

                  {detail.phoneNumbers.map((p, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <a
                        href={`tel:${p.number}`}
                        className="text-sm text-sca-purple-600 hover:text-sca-purple-700 hover:underline"
                      >
                        {p.number}
                      </a>
                      {p.label && (
                        <span className="text-xs text-gray-400">{p.label}</span>
                      )}
                    </div>
                  ))}

                  {addressLines.length > 0 && (
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {addressLines.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Tags */}
              {detail.tags.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Tag className="h-3.5 w-3.5" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-sca-purple-50 text-sca-purple-700 text-xs font-medium rounded-full border border-sca-purple-100"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Memberships */}
              <section>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  Memberships
                </h3>
                {detail.memberships.length === 0 ? (
                  <p className="text-sm text-gray-400">No memberships found</p>
                ) : (
                  <div className="space-y-3">
                    {detail.memberships.map((m) => (
                      <div
                        key={m.id}
                        className="rounded-lg border border-gray-200 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900">{m.planName}</p>
                          <MembershipStatusBadge status={m.status} />
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                          {m.startDate && (
                            <div>
                              <p className="text-xs text-gray-400">Start</p>
                              <p className="text-xs font-medium text-gray-700">{formatDate(m.startDate)}</p>
                            </div>
                          )}
                          {m.endDate && (
                            <div>
                              <p className="text-xs text-gray-400">End</p>
                              <p className="text-xs font-medium text-gray-700">{formatDate(m.endDate)}</p>
                            </div>
                          )}
                          {m.autoRenew !== null && (
                            <div>
                              <p className="text-xs text-gray-400">Auto-renew</p>
                              <p className="text-xs font-medium text-gray-700">{m.autoRenew ? 'Yes' : 'No'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Automations */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Zap className="h-3.5 w-3.5" />
                    Automations
                  </h3>
                  {canEdit && (
                    <button
                      onClick={() => setShowAutomationModal(true)}
                      className="text-xs font-medium text-sca-purple-600 hover:text-sca-purple-700"
                    >
                      + Trigger
                    </button>
                  )}
                </div>

                {/* Currently running */}
                {isPolling && pollingRecord && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg mb-2 text-sm text-sca-purple-700">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {pollingRecord.status === 'processing'
                      ? `Sending ${pollingRecord.automationName}...`
                      : `${pollingRecord.automationName} sent — waiting for completion...`}
                  </div>
                )}

                {loadingRecords ? (
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading...
                  </div>
                ) : records.length === 0 && !isPolling ? (
                  <p className="text-sm text-gray-400">No automations triggered yet</p>
                ) : (
                  <div className="space-y-2">
                    {records.map((rec) => (
                      <div
                        key={rec.id}
                        className="rounded-lg border border-gray-200 p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900">{rec.automationName}</p>
                          {rec.status === 'completed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle2 className="h-3 w-3" />
                              Completed
                            </span>
                          ) : rec.status === 'failed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <XCircle className="h-3 w-3" />
                              Failed
                            </span>
                          ) : rec.status === 'sent' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              <Clock className="h-3 w-3" />
                              Sent
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Sending
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 flex items-center justify-between">
                          <p className="text-xs text-gray-400">
                            {formatDate(rec.triggeredAt)} · {rec.triggeredByName}
                          </p>
                          <div className="flex items-center gap-1.5">
                            {rec.status === 'completed' && (
                              <span className="text-xs text-gray-400">Sent via email</span>
                            )}
                            {rec.status === 'failed' && canEdit && (
                              <button
                                onClick={() => setShowAutomationModal(true)}
                                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-amber-600"
                                title="Retry"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        {rec.status === 'completed' && rec.statusMessage && (
                          <p className="mt-1 text-xs text-gray-500">{rec.statusMessage}</p>
                        )}
                        {rec.status === 'failed' && rec.errorMessage && (
                          <p className="mt-1 text-xs text-red-500">{rec.errorMessage}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Record metadata */}
              <section className="border-t border-gray-100 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Added</p>
                    <p className="text-xs font-medium text-gray-600">{formatDate(detail.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Last modified</p>
                    <p className="text-xs font-medium text-gray-600">{formatDate(detail.updatedAt)}</p>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>

        {/* Footer — open in Beacon link */}
        {beaconUrl && (
          <div className="flex-shrink-0 border-t border-gray-200 p-4">
            <a
              href={beaconUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-sca-purple-600 hover:text-sca-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
            >
              Open full record in Beacon CRM
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
      </div>

      {/* Automation trigger modal */}
      {personId && detail && (
        <TriggerAutomationModal
          personId={personId}
          personName={detail.name}
          isOpen={showAutomationModal}
          onClose={() => setShowAutomationModal(false)}
          onTriggered={handleTriggered}
        />
      )}
    </>
  );
}

